/**
 * Life4Earth V2 — Admin API layer
 *
 * Thin wrappers around /api/admin/* endpoints.
 * All persistence is flat-file JSON on the backend.
 */

import { apiFetch, API_BASE_URL } from './client';
import type { LandingContent } from '../Data/Landing/landingContent';

// ── Landing Content ──────────────────────────────────────────

export async function fetchLandingContent(): Promise<LandingContent | null> {
  const result = await apiFetch<LandingContent | undefined>('/api/admin/landing-content');
  return result ?? null;
}

export async function saveLandingContent(content: LandingContent): Promise<void> {
  await apiFetch<{ saved: boolean }>('/api/admin/landing-content', {
    method: 'PUT',
    body: JSON.stringify(content),
  });
}

// ── Feature Flags ────────────────────────────────────────────

export interface FeatureFlags {
  [key: string]: boolean;
}

export async function fetchFeatureFlags(): Promise<FeatureFlags> {
  const result = await apiFetch<FeatureFlags | undefined>('/api/admin/feature-flags');
  return result ?? {};
}

export async function saveFeatureFlags(flags: FeatureFlags): Promise<void> {
  await apiFetch<{ saved: boolean }>('/api/admin/feature-flags', {
    method: 'PUT',
    body: JSON.stringify(flags),
  });
}

// ── Landing Image Upload ────────────────────────────────────

export type UploadCategory = 'team' | 'partners' | 'media';

/**
 * Upload an image for the landing page.
 *
 * For team/partners, `label` is required and drives unique naming:
 *   "Skip Davis" → SkipDavis.001.jpg, SkipDavis.002.jpg, etc.
 * For media, original filename is preserved.
 *
 * Returns the public URL path (e.g. "/content/images/team/SkipDavis.001.jpg").
 */
export async function uploadLandingImage(
  file: File,
  category: UploadCategory,
  label?: string,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams({ category });
  if (label) params.set('label', label);

  const res = await fetch(
    `${API_BASE_URL}/api/admin/upload-landing-image?${params}`,
    { method: 'POST', body: formData },
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as any).error || `Upload failed (${res.status})`);
  }

  const data = await res.json();
  return (data as any).path;
}

// ── Generic Config ───────────────────────────────────────────

export async function fetchAdminConfig<T = unknown>(key: string): Promise<T | null> {
  const result = await apiFetch<T | undefined>(`/api/admin/config/${encodeURIComponent(key)}`);
  return result ?? null;
}

export async function saveAdminConfig<T = unknown>(key: string, data: T): Promise<void> {
  await apiFetch<{ saved: boolean }>(`/api/admin/config/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}
